import React, { useState, Suspense } from 'react';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import { ClientOnly } from 'vite-react-ssg';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, MapPin, Calendar, Mountain, Star,
  ArrowRight, Target, Camera, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { BortleBadge } from '@/components/locations/BortleBadge';
import { NearbyLocations } from '@/components/locations/NearbyLocations';
import { NearbyWorkshops } from '@/components/workshops/NearbyWorkshops';
import type { LocationPageData } from '@/lib/locations';
import { placeTrail } from '@/utils/locationHubs';
import { locationDescription, locationTitle } from '@/utils/locationSeo';
import { descriptionToParagraphs } from '@/utils/locationDescription';
import { MilkyWayCalendar as CalendarComponent } from '@/components/MilkyWayCalendar';
import { CreateAnnualCalendarModal } from '@/components/CreateAnnualCalendarModal';
const LocationMap = React.lazy(() =>
  import('@/components/dashboard/LocationMap').then((m) => ({ default: m.LocationMap })),
);
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function LocationDetail() {
  // From the route loader, which reads the committed snapshot at build time, so
  // the page's text, title and links are in the HTML search engines get (ERI-2594).
  const { location, nearby } = useLoaderData() as LocationPageData;
  const navigate = useNavigate();
  const { user, isSubscribed } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth());
  const [creatingCalendar, setCreatingCalendar] = useState(false);
  const [showAnnualCalendarModal, setShowAnnualCalendarModal] = useState(false);
  const currentYear = new Date().getFullYear();

  const handleCreateAnnualCalendar = async () => {
    if (!location) return;
    if (isSubscribed && user?.email) {
      setCreatingCalendar(true);
      try {
        const { data, error } = await supabase.functions.invoke('create-shared-calendar', {
          body: {
            email: user.email,
            location_name: location.name,
            latitude: location.latitude,
            longitude: location.longitude,
            year: currentYear,
            user_id: user.id,
            is_premium: true,
            subscribe_newsletter: false,
          },
        });
        if (error) throw error;
        if (data?.slug) {
          toast.success('Annual calendar created!');
          navigate(`/calendar/${data.slug}`);
        }
      } catch (error) {
        console.error('Error creating calendar:', error);
        toast.error('Failed to create calendar');
      } finally {
        setCreatingCalendar(false);
      }
    } else {
      setShowAnnualCalendarModal(true);
    }
  };

  if (!location) {
    return (
      <>
        <SEOHead title="Location Not Found | Milky Way Planner" noindex />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-4">
            <h1 className="font-serif text-2xl font-bold text-foreground">Location not found</h1>
            <p className="text-muted-foreground">This dark sky location may have been removed or the link is incorrect.</p>
            <Link to="/dark-sky-locations">
              <Button variant="outline" className="gap-2 rounded-[4px]">
                <ArrowLeft className="w-4 h-4" /> Back to Locations
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  const planUrl = `/planner?name=${encodeURIComponent(location.name)}&lat=${location.latitude}&lon=${location.longitude}`;
  const bestMonthsStr = location.best_months?.map((m) => MONTH_SHORT[m - 1]).join(', ');
  const descriptionParagraphs = descriptionToParagraphs(location.description);

  const seoTitle = locationTitle(location.name);
  const seoDescription = locationDescription(location);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: location.name,
    description: seoDescription,
    geo: {
      '@type': 'GeoCoordinates',
      latitude: location.latitude,
      longitude: location.longitude,
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: location.country,
      ...(location.state_province && { addressRegion: location.state_province }),
    },
  };

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={`/dark-sky-locations/${location.slug}`}
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">

          {/* Back to the list, then up to the country and state hubs (ERI-2853). */}
          <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <li>
                <Link to="/dark-sky-locations" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Dark Sky Locations
                </Link>
              </li>
              {placeTrail(location).map((place) => (
                <li key={place.path} className="flex items-center gap-2">
                  <span aria-hidden="true">/</span>
                  <Link to={place.path} className="hover:text-foreground transition-colors">{place.name}</Link>
                </li>
              ))}
            </ol>
          </nav>

          {/* Header */}
          <div className="mb-6">
            {location.bortle_class && (
              <div className="mb-2">
                <BortleBadge bortle={location.bortle_class} />
              </div>
            )}
            <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-1">
              {location.name}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                {location.state_province ? `${location.state_province}, ` : ''}
                {location.country}
              </span>
              <span>{location.latitude.toFixed(4)}°, {location.longitude.toFixed(4)}°</span>
              {location.peak_core_altitude && (
                <span>Peak altitude {Number(location.peak_core_altitude).toFixed(0)}°</span>
              )}
              {location.darkness_hours_peak && (
                <span>{Number(location.darkness_hours_peak).toFixed(1)}h peak darkness</span>
              )}
              {bestMonthsStr && (
                <span>Best: {bestMonthsStr}</span>
              )}
            </div>
          </div>

          {/* Description: researched text from the snapshot, in the built HTML
              (ERI-2853). Blank lines in the text separate paragraphs. */}
          {descriptionParagraphs.length > 0 && (
            <section className="mb-8 max-w-3xl" aria-labelledby="location-about" data-testid="location-description">
              <h2 id="location-about" className="text-xl font-semibold text-foreground mb-3">
                About {location.name}
              </h2>
              <div className="space-y-4 text-base leading-relaxed text-foreground/90">
                {descriptionParagraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>
          )}

          {/* Map */}
          <div className="mb-8">
            {/* Leaflet needs a browser, so the map renders after load (ERI-2594). */}
            <ClientOnly fallback={<div className="h-[400px] rounded-xl bg-muted animate-pulse" />}>
              {() => (
                <Suspense fallback={<div className="h-[400px] rounded-xl bg-muted animate-pulse" />}>
                  <LocationMap
                    latitude={location.latitude}
                    longitude={location.longitude}
                    locationName={location.name}
                    hideEmptyPrompt
                  />
                </Suspense>
              )}
            </ClientOnly>
          </div>

          {/* Milky Way Calendar */}
          <div className="mb-8">
            <div className="border-b border-border pb-4 mb-4">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#c9922a]" />
                    Milky Way Calendar
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Nightly visibility ratings for {location.name}
                  </p>
                </div>
                <Button
                  onClick={handleCreateAnnualCalendar}
                  disabled={creatingCalendar}
                  size="sm"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {creatingCalendar ? 'Creating...' : 'Create Annual Calendar'}
                </Button>
              </div>
            </div>
            {/* The calendar depends on today's date, so it renders in the browser only;
                built at build time it would disagree with the visitor's date. */}
            <ClientOnly fallback={<div className="h-[420px] rounded-xl bg-muted/40" />}>
              {() => (
            <Tabs value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(Number(v))}>
              <TabsList className="grid grid-cols-6 lg:grid-cols-12 gap-1 h-auto p-1 mb-6">
                {MONTH_NAMES.map((_, i) => (
                  <TabsTrigger
                    key={i}
                    value={i.toString()}
                    className="text-xs px-2 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    {MONTH_SHORT[i]}
                  </TabsTrigger>
                ))}
              </TabsList>
              {MONTH_NAMES.map((_, i) => (
                <TabsContent key={i} value={i.toString()} className="mt-0">
                  <CalendarComponent
                    location={{
                      name: location.name,
                      latitude: location.latitude,
                      longitude: location.longitude,
                    }}
                    month={i}
                    year={currentYear}
                    isYearlyView={true}
                    isMarketingPreview={true}
                  />
                </TabsContent>
              ))}
            </Tabs>
              )}
            </ClientOnly>
          </div>

          {/* Recognition info */}
          {(location.recognition_year || location.recognizing_body || location.dsag_class || location.area_hectares) && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-[#c9922a]" />
                Recognition
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {location.recognition_year && (
                  <div className="p-4 bg-secondary/20 rounded-lg">
                    <span className="text-muted-foreground">Year designated</span>
                    <p className="font-semibold text-foreground mt-1">{location.recognition_year}</p>
                  </div>
                )}
                {location.recognizing_body && (
                  <div className="p-4 bg-secondary/20 rounded-lg">
                    <span className="text-muted-foreground">Recognized by</span>
                    <p className="font-semibold text-foreground mt-1">{location.recognizing_body}</p>
                  </div>
                )}
                {location.dsag_class && (
                  <div className="p-4 bg-secondary/20 rounded-lg">
                    <span className="text-muted-foreground">DSAG class</span>
                    <p className="font-semibold text-foreground mt-1">{location.dsag_class}</p>
                  </div>
                )}
                {location.area_hectares && (
                  <div className="p-4 bg-secondary/20 rounded-lg">
                    <span className="text-muted-foreground">Area</span>
                    <p className="font-semibold text-foreground mt-1">{Number(location.area_hectares).toLocaleString()} ha</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Nearby dark sky locations */}
          <div className="mb-8">
            <NearbyLocations locations={nearby} locationName={location.name} />
          </div>

          {/* Nearby workshops */}
          <div className="mb-8">
            <NearbyWorkshops
              latitude={location.latitude}
              longitude={location.longitude}
              locationName={location.name}
            />
          </div>

          {/* CTA cards */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to={planUrl} className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Plan a Shoot</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Open in the planner with this location pre-filled.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Open Planner <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            <Link to="/workshops" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Browse Workshops</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Astrophotography workshops at dark sky locations.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Find a Workshop <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            <Link to="/features" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Mountain className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Go Premium</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Weather forecasts, PDF exports, and more.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Learn More <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          </div>

        </div>
      </div>

      <CreateAnnualCalendarModal
        location={{
          name: location.name,
          latitude: location.latitude,
          longitude: location.longitude,
        }}
        selectedYear={currentYear}
        isOpen={showAnnualCalendarModal}
        onClose={() => setShowAnnualCalendarModal(false)}
      />
    </>
  );
}
